

import { Edit as Edit3 } from '@mui/icons-material';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { Handle, Position } from '@xyflow/react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { STATUS_COLORS } from '../constants';

import type { SectionNodeData } from '../types';

interface SectionNodeProps {
  data: SectionNodeData;
  selected?: boolean;
}

// Handle styles for visible vs invisible handles
const invisibleHandleStyle = { opacity: 0 };
const visibleHandleStyle = {
  width: 12,
  height: 12,
  background: '#94a3b8',
  border: '2px solid white',
  boxShadow: '0 2px 4px rgba(0,0,0,0.25)',
};

/**
 * Section node with editable title (on hover edit button) and status-based coloring
 */
export function SectionNode({ data, selected }: SectionNodeProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [showHandlesDelayed, setShowHandlesDelayed] = useState(false);
  const [editValue, setEditValue] = useState(data.label);
  // Local display label - updates immediately on save, then syncs with data.label
  const [displayLabel, setDisplayLabel] = useState(data.label);
  const inputRef = useRef<HTMLInputElement>(null);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const statusConfig = STATUS_COLORS[data.status] || STATUS_COLORS.notWritten;

  // Handle delayed hiding of handles - stay visible for 500ms after hover ends
  useEffect(() => {
    if (isHovered && data.isEditable) {
      // Clear any pending hide timeout
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = null;
      }
      setShowHandlesDelayed(true);
    } else if (!isHovered && showHandlesDelayed) {
      // Delay hiding handles by 500ms
      hideTimeoutRef.current = setTimeout(() => {
        setShowHandlesDelayed(false);
      }, 5000);
    }
    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, [isHovered, data.isEditable, showHandlesDelayed]);

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  // Sync displayLabel and editValue with label when data changes (from parent)
  useEffect(() => {
    if (!isEditing) {
      setEditValue(data.label);
      setDisplayLabel(data.label);
    }
  }, [data.label, isEditing]);

  const handleEditClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setIsEditing(true);
    setEditValue(displayLabel);
  }, [displayLabel]);

  const handleSave = useCallback(() => {
    const newLabel = editValue.trim();
    if (newLabel && newLabel !== data.label) {
      // Update local display immediately
      setDisplayLabel(newLabel);
      // Notify parent (this will trigger database update)
      data.onLabelChange?.(newLabel);
    }
    setIsEditing(false);
  }, [editValue, data]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    e.stopPropagation();
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      setEditValue(displayLabel);
      setIsEditing(false);
    }
  }, [handleSave, displayLabel]);

  const handleInputClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  // Show visible handles when editable (custom mode) - uses delayed hide
  const showHandles = showHandlesDelayed;

  return (
    <>
      {/* Hidden handle for floating edges logic */}
      <Handle
        type="source"
        position={Position.Top}
        id="floating"
        style={{ opacity: 0, top: '50%', left: '50%' }}
      />
      <Handle
        type="target"
        position={Position.Top}
        id="floating"
        style={{ opacity: 0, top: '50%', left: '50%' }}
      />

      {/* Handles on all 4 sides for flexible edge routing */}
      <Handle
        type="target"
        position={Position.Top}
        id="top-target"
        style={showHandles ? visibleHandleStyle : invisibleHandleStyle}
      />
      <Handle
        type="source"
        position={Position.Top}
        id="top-source"
        style={showHandles ? { ...visibleHandleStyle, background: '#3b82f6' } : invisibleHandleStyle}
      />
      <Handle
        type="target"
        position={Position.Right}
        id="right-target"
        style={showHandles ? visibleHandleStyle : invisibleHandleStyle}
      />
      <Handle
        type="source"
        position={Position.Right}
        id="right-source"
        style={showHandles ? { ...visibleHandleStyle, background: '#3b82f6' } : invisibleHandleStyle}
      />
      <Handle
        type="target"
        position={Position.Bottom}
        id="bottom-target"
        style={showHandles ? visibleHandleStyle : invisibleHandleStyle}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="bottom-source"
        style={showHandles ? { ...visibleHandleStyle, background: '#3b82f6' } : invisibleHandleStyle}
      />
      <Handle
        type="target"
        position={Position.Left}
        id="left-target"
        style={showHandles ? visibleHandleStyle : invisibleHandleStyle}
      />
      <Handle
        type="source"
        position={Position.Left}
        id="left-source"
        style={showHandles ? { ...visibleHandleStyle, background: '#3b82f6' } : invisibleHandleStyle}
      />

      <Paper
        elevation={selected ? 8 : 2}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        sx={{
          p: 2,
          minWidth: 140,
          maxWidth: 180,
          background: `
            linear-gradient(90deg, ${statusConfig.primary} 0%, ${statusConfig.primary}80 100%) 0 0 / 100% 4px no-repeat,
            linear-gradient(145deg, ${statusConfig.bg} 0%, ${statusConfig.primary}08 100%)
          `,

          backgroundSize: `100% 4px, 100% 100%`,
          backgroundPosition: `0 0, 0 0`,
          backgroundRepeat: `no-repeat, no-repeat`,

          backgroundOrigin: `padding-box, border-box`,
          backgroundClip: `padding-box, border-box`,
          backdropFilter: 'blur(10px)',
          border: 2,
          borderColor: selected ? statusConfig.primary : `${statusConfig.primary}80`,
          borderRadius: 3,
          cursor: 'pointer',
          transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
          textAlign: 'center',
          position: 'relative',
          overflow: 'visible',
          '&:hover': {
            transform: 'translateY(-4px) scale(1.02)',
            boxShadow: `0 12px 28px ${statusConfig.primary}30`,
            borderColor: statusConfig.primary,
          },
        }}
      >
        {/* Edit button - appears on hover when editable */}
        {data.isEditable && isHovered && !isEditing && (
          <IconButton
            size="small"
            onClick={handleEditClick}
            sx={{
              position: 'absolute',
              top: -8,
              right: -8,
              bgcolor: 'white',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              width: 24,
              height: 24,
              zIndex: 10,
              '&:hover': {
                bgcolor: 'grey.100',
              },
            }}
          >
            <Edit3 sx={{ fontSize: 12 }} />
          </IconButton>
        )}

        {isEditing ? (
          <input
            ref={inputRef}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onClick={handleInputClick}
            onBlur={handleSave}
            onKeyDown={handleKeyDown}
            style={{
              width: '100%',
              border: `2px solid ${statusConfig.primary}`,
              borderRadius: 4,
              background: 'white',
              textAlign: 'center',
              fontSize: '0.875rem',
              fontWeight: 700,
              color: statusConfig.text,
              outline: 'none',
              padding: '4px 8px',
            }}
          />
        ) : (
          <Typography
            variant="body2"
            fontWeight={700}
            sx={{
              color: statusConfig.text,
              lineHeight: 1.3,
              mb: 0.5,
            }}
          >
            {displayLabel}
          </Typography>
        )}
        <Chip
          size="small"
          label={`${data.eventCount} event${data.eventCount !== 1 ? 's' : ''}`}
          sx={{
            height: 20,
            fontSize: '0.7rem',
            bgcolor: `${statusConfig.primary}20`,
            color: statusConfig.text,
            fontWeight: 500,
            border: `1px solid ${statusConfig.primary}40`,
          }}
        />
      </Paper>
    </>
  );
}

export default SectionNode;
